import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { formatDate, formatMoney } from "@bw/shared/utils";
import { StatusBadge } from "@/components/StatusBadge";
import type { PayoutLedgerEntryResponse } from "../api";

/**
 * Platform payout history. The parent owns data fetching; each row renders only
 * backend values and uses its own currency before the envelope fallback.
 */
export function PayoutLedger({
  isLoading,
  payouts,
  currency,
  total,
}: {
  isLoading: boolean;
  payouts: PayoutLedgerEntryResponse[];
  currency: string | null;
  total: number;
}) {
  if (isLoading) {
    return (
      <Card variant="outlined">
        <Box
          role="status"
          aria-label="Loading payout history"
          sx={{ display: "flex", justifyContent: "center", py: 6 }}
        >
          <CircularProgress size={32} />
        </Box>
      </Card>
    );
  }

  if (payouts.length === 0) {
    return (
      <Card variant="outlined">
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ px: 2.5, py: 4 }}
        >
          No payouts yet.
        </Typography>
      </Card>
    );
  }

  const showingHint =
    total > payouts.length ? `Showing ${payouts.length} of ${total}` : null;

  return (
    <Card component="section" variant="outlined">
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          px: 2.5,
          py: 2,
        }}
      >
        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Payout history
        </Typography>
        {showingHint && (
          <Typography color="text.secondary" variant="caption">
            {showingHint}
          </Typography>
        )}
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell scope="col">Payout</TableCell>
              <TableCell scope="col" align="right">
                Amount
              </TableCell>
              <TableCell scope="col">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((payout) => {
              const rowCurrency = payout.currency ?? currency ?? "USD";
              const dateLabel = payout.arrival_date
                ? `Paid out ${formatDate(payout.arrival_date)}`
                : "Pending";

              return (
                <TableRow key={payout.payout_id} hover>
                  <TableCell>{dateLabel}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(payout.amount, rowCurrency)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={payout.status} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
